import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select } from "chakra-react-select";
import { endOfMonth, format, subMonths } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import {
  fetchPPLList,
  triggerInsightCalculation,
} from "../../api/industryInsights";
import { Context, useCurrentProfile } from "../../ContextWrapper";
import CustomTableContainer from "../../theme/components/tableContainer";
import { GenericModal } from "../generic/modal";
import { InsightCustomerListing } from "./InsightCustomerListing";
import { InsightValuesBreakdown } from "./insightValuesBreakdown";

export const InsightListing = () => {
  const [loading, setLoading] = useState(true);
  const [pplList, setPPLList] = useState([]);
  const [toggleEdit, setToggleEdit] = useState(false);
  const [selectedPPL, setSelectedPPL] = useState();
  const [selectedMonth, setSelectedMonth] = useState();
  const [toggleAssignCustomers, setToggleAssignCustomers] = useState(false);
  const { profile } = useCurrentProfile();

  const {
    isOpen: isTaskOpen,
    onOpen: onTaskOpen,
    onClose: onTaskClose,
  } = useDisclosure();

  const context = React.useContext(Context);

  useEffect(() => {
    const refreshPPLList = () => {
      fetchPPLList()
        .then((data) => {
          setPPLList(data);
        })
        .catch((err) => {
          console.error("Failed to fetch PPL List:", err);
        });

      setLoading(false);
    };

    refreshPPLList();
  }, []);

  const handleSubmit = async () => {
    if (!selectedMonth) {
      return context.fireToast("Missing", "Please select a month", "warning");
    }

    try {
      await triggerInsightCalculation(selectedMonth);
      context.fireToast("Success", "Calculation triggered", "success");
      onTaskClose();
    } catch (error) {
      console.error("Error triggering calculation:", error);
      context.fireToast("Error", "Something went wrong", "error");
    }
  };

  const toggleView = () => {
    setToggleEdit(true);
  };

  return (
    <Container maxW="100%" bg="brand.50" h="calc(91vh)">
      <TaskModal
        isOpen={isTaskOpen}
        onClose={onTaskClose}
        setSelectedMonth={setSelectedMonth}
        handleSubmit={handleSubmit}
      />
      {!toggleEdit && !toggleAssignCustomers && (
        <>
          {loading && (
            <FontAwesomeIcon
              className="float-end fa-spin"
              icon={faSpinner}
              color="green"
            />
          )}
          <Stack direction="column" spacing={2} align="center">
            <CustomTableContainer width="100%">
              <Flex pb="5">
                <Box>
                  <Heading as="h3" size="lg">
                    Manage Industry Insights
                  </Heading>
                </Box>
                <Spacer />
                <Box>
                  <Stack direction="row" spacing={6} align="center">
                    {profile.type === "admin" && (
                      <>
                        <Button
                          onClick={() =>
                            setToggleAssignCustomers(!toggleAssignCustomers)
                          }
                        >
                          Assign customers for insights
                        </Button>
                        <Button onClick={onTaskOpen}>
                          Update Industry Insights
                        </Button>
                      </>
                    )}
                  </Stack>
                </Box>
              </Flex>
              <Box
                maxHeight="calc(100vh - 350px)"
                bg="white"
                overflowY={"auto"}
              >
                <Table variant="unstyled" overflowY={"auto"}>
                  <Thead position="sticky" top={-1} zIndex={1}>
                    <Tr>
                      <Th>Performance</Th>
                      <Th>Threshold</Th>
                      <Th>Action</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pplList.map((ppl) => (
                      <PPLRow
                        key={ppl.id}
                        ppl={ppl}
                        toggle={toggleView}
                        setPPL={setSelectedPPL}
                      />
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CustomTableContainer>
          </Stack>
        </>
      )}

      {toggleEdit && (
        <InsightValuesBreakdown
          insight_id={selectedPPL}
          turnBack={setToggleEdit}
        />
      )}

      {toggleAssignCustomers && (
        <InsightCustomerListing turnBack={setToggleAssignCustomers} />
      )}
    </Container>
  );
};

const PPLRow = ({ ppl, toggle, setPPL }) => {
  const performanceGroupMap = useMemo(
    () => ({
      average: "Average Performers",
      ten: "Top 10% Performers",
      one: "Top 1% Performers",
    }),
    []
  );
  const revenueGroupMap = useMemo(
    () => ({
      fiftyk: "Under $50k",
      hundredk: "$50k–$100k",
      onefiftyk: "$100k–$150k",
      twofiftyk: "$150k–$250k",
      overtwofiftyk: "Over $250k",
    }),
    []
  );
  return (
    <>
      <Tr key={ppl.id}>
        <Td>
          {performanceGroupMap[ppl.performance_group] || ppl.performance_group}
        </Td>
        <Td>{revenueGroupMap[ppl.revenue_group] || ppl.revenue_group}</Td>
        <Td>
          <HStack spacing={2}>
            <Button
              onClick={() => {
                setPPL(ppl.id);
                toggle();
              }}
            >
              Edit
            </Button>
          </HStack>
        </Td>
      </Tr>
    </>
  );
};

const TaskModal = ({ isOpen, onClose, setSelectedMonth, handleSubmit }) => {
  const past12Months = Array.from({ length: 12 }, (_, i) => {
    const date = endOfMonth(subMonths(new Date(), i + 1));
    return {
      label: format(date, "MMMM yyyy"),
      value: format(date, "yyyy-MM-dd"),
    };
  });

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Industry Insights"
      body={
        <>
          <Text pb={"10px"}>Update Insights up to month</Text>
          <Select
            options={past12Months}
            placeholder="Select a month"
            onChange={(option) => setSelectedMonth(option.value)}
            useBasicStyles
          />
        </>
      }
      submitButton={
        <Button onClick={handleSubmit} ml={3}>
          Update
        </Button>
      }
    ></GenericModal>
  );
};
